import React, { DragEventHandler, createContext, useCallback, useContext, useMemo, useRef, useState, MutableRefObject, Dispatch, SetStateAction } from 'react';
import { Box, Typography, Paper, Divider, Button, IconButton, SxProps, Theme, Tooltip } from '@mui/material';
import { TreeView, TreeItem } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { DialogContext, DocumentFormTabs } from '../documents/fs/DocumentForm';
import { commentsSelectors, isDocEditable, sectionSelectors, selectDocument } from '../documents/documentSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { impossible } from '../../app/Utils';
import { SectionContentDispatcher } from './SectionContentDispatcher';
import { AppId } from '../../app/appTypes';
import { deleteDocSection, moveDocSection } from './documentsApi';
import { unwrapResult } from '@reduxjs/toolkit';
import { Localized } from "@fluent/react";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import NewSmallBadge from '../../app/components/NewSmallBadge';
import TotalSmallBadge from '../../app/components/TotalSmallBadge';
import ChildrenCommentsBadge from '../../app/components/ChildrenCommentsBadge';
import { selectAllURSComments } from '../urs/URSCommentSlice'
import { store } from '../../app/store';

export const SectionDeleteContext = createContext<(sectionId: AppId) => void>(x => { })

const maxTreeLvl: number = 6

export interface DocumentContentProps {
    switchTab: (tab: DocumentFormTabs) => void
    selectedNode: string | null
    setSelectedNode: Dispatch<SetStateAction<string | null>>
    tocCollapsed: boolean
    setTocCollapsed: Dispatch<SetStateAction<boolean>>
}

export const DocumentContent = (props: DocumentContentProps) => {
    const dispatch = useAppDispatch()
    const { selectedNode, setSelectedNode, tocCollapsed, setTocCollapsed } = props
    const [nextId, setNextId] = useState(12);

    const selectNode = useCallback((nodeId: string) => {
        const section = sectionSelectors.selectById(store.getState(), nodeId)
        if (section && section.type === 'RISK_ANALYSIS') {
            props.switchTab('ra')
        }
        else if (section && section.type === 'REQUIREMENTS') {
            props.switchTab('requirements')
        }
        else {
            setSelectedNode(nodeId)
        }
    }, [])

    const deleteSection = useCallback(async (id: AppId) => {
        try {
            const state = store.getState()
            const section = sectionSelectors.selectById(state, id)

            if (section) {
                await dispatch(
                    deleteDocSection({
                        documentId: section.documentId,
                        sectionId: id,
                    })
                ).unwrap()

                setSelectedNode(null)
            }

        } catch (error) {
            throw error
        }
    }, [dispatch, setSelectedNode])

    const handleAddChildItem = (parentId: number) => {
        const addChild = (nodes: any[]): any[] => {
            return nodes.map(node => {
                if (node.id === parentId) {
                    const newChildId = nextId;
                    const newChild = { id: newChildId, label: `New Item ${newChildId}` };
                    setNextId(nextId + 1);
                    return {
                        ...node,
                        children: node.children ? [...node.children, newChild] : [newChild]
                    };
                }
                if (node.children) {
                    return { ...node, children: addChild(node.children) };
                }
                return node;
            });
        };
    };

    const findNodeLabel = (id: string, nodes: any[]): string | null => {

        for (const node of nodes) {
            if (node.id.toString() == id) return node.label;
            if (node.children) {
                const foundLabel = findNodeLabel(id, node.children);
                if (foundLabel) return foundLabel;
            }
        }
        return null;
    };

    const renderView = () => {
        if (!selectedNode) {
            return <Typography variant="body1" color="textSecondary">Select an item from the structure to see details.</Typography>;
        }
        return <SectionContentDispatcher sectionId={selectedNode} />;
    };

    return (
        <SectionDeleteContext.Provider value={deleteSection}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: tocCollapsed ? '48px minmax(0, 1fr)' : 'minmax(260px, 1fr) 3fr',
                gridTemplateRows: 'calc(100vh - 280px)',
                gap: 2,
                [`.tiptap`]: {
                    height: 'calc(100vh - 480px)',
                    overflow: 'auto',
                },
            }}>
                <Paper elevation={3} sx={{
                    overflow: 'auto',
                    padding: tocCollapsed ? 0.5 : 2,
                }}>
                    <TableOfContents
                        selectNode={selectNode}
                        collapsed={tocCollapsed}
                        onToggleCollapsed={() => setTocCollapsed((prev) => !prev)}
                    />
                </Paper>
                <Paper elevation={3} sx={{
                    padding: 2,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}>
                    {renderView()}
                </Paper>
            </Box>
        </SectionDeleteContext.Provider>
    );
};

type DraggedItem = { sectionId: string, parentId: string }

const TableOfContents = (props: { selectNode: (nodeId: string) => void, collapsed: boolean, onToggleCollapsed: () => void }) => {
    const { selectNode, collapsed, onToggleCollapsed } = props
    const dialog = useContext(DialogContext)
    const editable = useAppSelector(isDocEditable)
    const draggedItem = useRef<DraggedItem | null>(null)
    const allRootSectionIds = useAppSelector(state => sectionSelectors.selectAll(state).filter(s => s.parentSectionGuid === null).map(s => s.id))
    const rootSections = useAppSelector(state => allRootSectionIds.map(id => sectionSelectors.selectById(state, id)).filter(Boolean))

    const [expandedNodes, setExpandedNodes] = useState<string[]>([])
    const expandedNodesSet = useMemo(() => new Set(expandedNodes), [expandedNodes])

    const handleNodeSelect = (event: React.SyntheticEvent, nodeId: string) => {
        selectNode(nodeId)
    }

    const handleNodeToggle = (_event: React.SyntheticEvent, nodeIds: string[]) => {
        setExpandedNodes(nodeIds)
    }

    const handleAddNewItem = () => {
        dialog({
            tag: 'add-section',
            parentSectionId: null,
        })
    };

    if (collapsed) {
        return <Box
            sx={{
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 0.5,
                pt: 0.5,
            }}
        >
            <Tooltip title="Rozwiń panel sekcji" placement="right">
                <IconButton size="small" onClick={onToggleCollapsed}>
                    <KeyboardDoubleArrowRightIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Divider sx={{ width: '100%', my: 0.25 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', alignItems: 'center' }}>
                {rootSections.map(section => {
                    if (!section) {
                        return null
                    }
                    return (
                        <Tooltip key={section.id} title={`${section.numeration}. ${section.name}`} placement="right">
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => selectNode(section.id)}
                                sx={{
                                    minWidth: 0,
                                    width: '100%',
                                    px: 0.25,
                                    py: 0.25,
                                    fontSize: 11,
                                    lineHeight: 1.2,
                                }}
                            >
                                {section.numeration}
                            </Button>
                        </Tooltip>
                    )
                })}
            </Box>
        </Box>
    }

    return <>
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: (t) => t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.grey[100],
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 1.5,
                py: 1,
            }}
        >
            <AccountTreeIcon sx={{ fontSize: 20, opacity: 0.9 }} />
            <Typography variant="h6">
                <Localized id="document-structure">Document Structure</Localized>
            </Typography>
            <Box sx={{ ml: 'auto' }}>
                <Tooltip title="Zwiń panel sekcji" placement="top">
                    <IconButton size="small" onClick={onToggleCollapsed}>
                        <KeyboardDoubleArrowLeftIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <TreeView onNodeSelect={handleNodeSelect} onNodeToggle={handleNodeToggle}
            expanded={expandedNodes} defaultCollapseIcon={<ExpandMore />}
            defaultExpandIcon={<ChevronRight />}>
            {allRootSectionIds.map(secId => <SectionTreeNode lvl={1} key={secId} sectionId={secId} parentSectionId='root' draggedItemRef={draggedItem} expandedNodes={expandedNodesSet} />)}
        </TreeView>
        {editable && <Button size='small' variant="contained" color="primary" sx={{ mt: 2 }}
            startIcon={<AddIcon />} onClick={handleAddNewItem}>
            <Localized id="doc-section-add-new">Add New Item</Localized>
        </Button>}
    </>
}

const getAllDescendantIds = (sectionId: string, state: any): string[] => {
    const section = sectionSelectors.selectById(state, sectionId)
    if (!section || section.subSectionsIds.length === 0) return []
    const result: string[] = []
    for (const childId of section.subSectionsIds) {
        result.push(childId, ...getAllDescendantIds(childId, state))
    }
    return result
}

const SectionTreeNode = (props: { sectionId: string, parentSectionId: string, draggedItemRef: MutableRefObject<DraggedItem | null>, lvl: number, expandedNodes: Set<string> }) => {
    const docState = useAppSelector(selectDocument)?.doc ?? impossible()
    const { draggedItemRef, lvl, expandedNodes } = props
    const dispatch = useAppDispatch()
    const dialog = useContext(DialogContext)
    const { sectionId, parentSectionId } = props
    const section = useAppSelector(state => sectionSelectors.selectById(state, sectionId)) ?? impossible()
    const newCommentsCount = useAppSelector(state => {
        if (section.type === 'REQUIREMENTS') {
            return selectAllURSComments(state).filter(c => c.parentCommentId === null && c.status === 'N').length
        } else {
            return commentsSelectors.selectAll(state).filter(c => c.sectionGuid === sectionId && c.status === "NEW").length
        }
    })
    const totalCommentsCount = useAppSelector(state => {
        if (section.type === 'REQUIREMENTS') {
            return selectAllURSComments(state).filter(c => c.parentCommentId === null).length
        } else {
            return commentsSelectors.selectAll(state).filter(c => c.sectionGuid === sectionId).length
        }
    })
    const { numeration, name, subSectionsIds } = section
    const childrenNewCommentsCount = useAppSelector(state => {
        if (subSectionsIds.length === 0) return 0
        const descendantIds = new Set(getAllDescendantIds(sectionId, state))
        return commentsSelectors.selectAll(state).filter(c => descendantIds.has(c.sectionGuid) && c.status === "NEW").length
    })
    const childrenTotalCommentsCount = useAppSelector(state => {
        if (subSectionsIds.length === 0) return 0
        const descendantIds = new Set(getAllDescendantIds(sectionId, state))
        return commentsSelectors.selectAll(state).filter(c => descendantIds.has(c.sectionGuid)).length
    })
    const editable = useAppSelector(isDocEditable)
    const isRiskSection = section.type === 'RISK_ANALYSIS'

    const [dragOver, setDragOver] = useState(false)

    const handleAddChildItem = () => {
        dialog({
            tag: 'add-section',
            parentSectionId: sectionId,
        })
    }

    const onDragOver: DragEventHandler<HTMLLIElement> = useCallback((e) => {
        e.preventDefault()
        if (draggedItemRef.current) {
            const { sectionId: draggedId, parentId } = draggedItemRef.current

            if (draggedId !== sectionId && parentId === parentSectionId) {
                setDragOver(true)
            }
        }
    }, [sectionId])

    const onDragStart: DragEventHandler<HTMLLIElement> = useCallback(e => {
        e.stopPropagation()
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData("text/plain", sectionId)
        draggedItemRef.current = { sectionId, parentId: parentSectionId }
    }, [sectionId])

    const onDragLeave: DragEventHandler<HTMLLIElement> = useCallback(() => {
        setDragOver(false)
    }, [sectionId])

    const onDrop: DragEventHandler<HTMLLIElement> = useCallback(e => {
        if (draggedItemRef.current) {
            const { sectionId: draggedId, parentId } = draggedItemRef.current
            if (parentSectionId === parentId && sectionId !== draggedId) {
                setDragOver(false)
                dispatch(moveDocSection({
                    documentId: docState.header.id,
                    sectionId: draggedId,
                    newParentId: parentSectionId === 'root' ? null : parentSectionId,
                    insertBeforeSectionId: sectionId,
                })).then(unwrapResult)
                    .finally(() => {
                        draggedItemRef.current = null
                    })
            }
        }
    }, [sectionId])

    let class_: SxProps<Theme> | undefined = undefined

    const visibility = dragOver ? 'visible' : 'hidden'

    const canAddSubsection = editable && lvl < maxTreeLvl && (section.type === 'GROUP' || section.type === 'RESPONSIBILITIES' || section.type === 'TESTS_METHODOLOGY' || section.type === 'VALIDATED_OBJECT')

    return <TreeItem
        sx={class_}
        key={sectionId}
        nodeId={sectionId}
        draggable={true}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}

        // this piece of 💩 is required in order to make d&d working on chrome
        // check out https://github.com/mui/material-ui/issues/29518
        onFocusCapture={e => e.stopPropagation()}

        label={<Box sx={{ position: 'relative', '&:hover .add-icon': { opacity: 1 } }}>
            <Box sx={{
                height: '2px',
                width: '100%',
                bgcolor: 'var(--purple-700)',
                visibility: visibility,
            }}></Box>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
            }}>
                <Typography>{numeration}. {name}</Typography>
                {newCommentsCount > 0 && <Tooltip title={<Localized id="doc-section-new-comments-tooltip">Unresolved comments</Localized>}><span><NewSmallBadge label={`${newCommentsCount}`} /></span></Tooltip>}
                {totalCommentsCount > 0 && <Tooltip title={<Localized id="doc-section-total-comments-tooltip">Total comments</Localized>}><span><TotalSmallBadge label={`${totalCommentsCount}`} /></span></Tooltip>}
                {childrenTotalCommentsCount > 0 && !expandedNodes.has(sectionId) && <Tooltip title={<Localized id="doc-section-children-comments-tooltip">Unresolved / total comments in subsections</Localized>}><span><ChildrenCommentsBadge label={`${childrenNewCommentsCount}/${childrenTotalCommentsCount}`} /></span></Tooltip>}
                {canAddSubsection && <IconButton size="small" sx={{ marginLeft: 1, position: 'absolute', right: 0, opacity: 0, transition: 'opacity 0.3s' }} className="add-icon" onClick={handleAddChildItem}>
                    <AddIcon fontSize="small" />
                </IconButton>}
            </Box>
        </Box>}>
        {!isRiskSection && subSectionsIds.map(childId => <SectionTreeNode lvl={lvl + 1} key={childId} sectionId={childId} parentSectionId={sectionId} draggedItemRef={draggedItemRef} expandedNodes={expandedNodes} />)}
    </TreeItem>
}
